import { getAuthorized } from "@/lib/fetch";
import { Select, SelectItem, SelectSection } from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { FieldValues, Path, PathValue } from "react-hook-form";
import { SectionSelectFieldProps } from "./types";

interface TranslationRecord {
  [key: string]: string;
}

interface TranslationResponse {
  [section: string]: TranslationRecord;
}

export function SelectSectionField<T extends FieldValues>({
  field,
  setValue,
  watch,
}: {
  field: SectionSelectFieldProps;
  setValue: (name: Path<T>, value: PathValue<T, Path<T>>) => void;
  watch: (name: Path<T>) => any;
}) {
  const { t, i18n } = useTranslation();
  const selectedValue = watch(field.name as Path<T>) || "";
  const [options, setOptions] = useState<
    Array<{
      section: string;
      items: Array<{ value: string; label: string }>;
    }>
  >([]);

  // this ref is used to desactivate the default right aligned listbox
  const listboxRef = useRef<HTMLDivElement>(null);

  // Fetch translations if useTranslation is true
  const { data: translations, isLoading: isLoadingTranslations } =
    useQuery<TranslationResponse>({
      queryKey: [
        field.table_name || "",
        "translations",
        field.language || "fr",
      ],
      queryFn: async () => {
        if (!field.useTranslation || !field.client || !field.table_name) {
          return {};
        }

        const res = await getAuthorized(
          `${import.meta.env.VITE_BACKEND_URL}/${field.client}/${
            field.table_name
          }/translations?language=${field.language || "fr"}`,
        );

        if (!res.ok) {
          throw new Error("Failed to fetch translations");
        }

        return res.json();
      },
      enabled: field.useTranslation === true,
    });

  // Fetch section name translations if useTransactionForSectionName is true
  const {
    data: sectionNameTranslations,
    isLoading: isLoadingSectionNameTranslations,
  } = useQuery<TranslationResponse>({
    queryKey: [
      field.section_name_table_name || "",
      "section-name-translations",
      field.language || "fr",
    ],
    queryFn: async () => {
      if (
        !field.useTransactionForSectionName ||
        !field.client ||
        !field.section_name_table_name
      ) {
        return {};
      }

      const res = await getAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/${field.client}/${
          field.section_name_table_name
        }/translations?language=${field.language || "fr"}`,
      );

      if (!res.ok) {
        throw new Error("Failed to fetch section name translations");
      }

      return res.json();
    },
    enabled: field.useTransactionForSectionName === true,
  });

  // Process options based on translations
  useEffect(() => {
    if (field.useTranslation && translations) {
      const section_key = field.translate_key || "";
      const translationSection = translations[section_key] || {};

      // Get section name translations if available
      const getSectionName = (sectionName: string) => {
        if (field.section_name_translation_prefix) {
          return t(`${field.section_name_translation_prefix}.${sectionName}`, {
            defaultValue: sectionName,
          });
        }
        if (field.useTransactionForSectionName && sectionNameTranslations) {
          const sectionKey = field.section_name_translation_key || "";
          const sectionTranslations = sectionNameTranslations[sectionKey] || {};
          return sectionTranslations[sectionName] || sectionName;
        }
        return sectionName;
      };

      // Transform options using translations
      const transformedOptions = field.options.map((section) => ({
        section: getSectionName(section.section_name),
        items: (section.fields || []).map((fieldKey) => ({
          value: fieldKey,
          label: translationSection[fieldKey] || fieldKey,
        })),
      }));

      setOptions(transformedOptions);
    } else if (!field.useTranslation) {
      // Use direct options if no translation
      const directOptions = field.options.map((section) => ({
        section: section.section_name,
        items: (section.options || []).map((option) => ({
          value: option.value,
          label: option.label,
        })),
      }));

      setOptions(directOptions);
    }
  }, [translations, sectionNameTranslations, field, t, i18n.language]);

  const isLoading =
    isLoadingTranslations ||
    (field.useTransactionForSectionName && isLoadingSectionNameTranslations);

  return (
    <Select
      size="sm"
      labelPlacement="outside"
      key={field.name}
      label={field.label}
      isLoading={isLoading}
      selectedKeys={selectedValue ? [selectedValue] : []}
      onChange={(e) => {
        const value = e.target.value;
        setValue(field.name as Path<T>, value as PathValue<T, Path<T>>);
      }}
      classNames={{
        trigger:
          "bg-white hover:bg-white hover:border-slate-400 data-[hover=true]:bg-white group-data-[focus=true]:bg-white group-data-[focus=true]:border-primary border-2 shadow-sm h-9",
        label: "text-blue-600 group-data-[filled-within=true]:text-blue-600",
      }}
      popoverProps={{
        classNames: {
          content:
            "w-auto max-w-[var(--radix-popper-available-width)] !min-w-[210px]",
        },
        ref: listboxRef,
        shouldFlip: true,
        offset: 5,
      }}
      listboxProps={{
        classNames: {
          base: "max-w-none w-auto",
        },
      }}
    >
      {options.map((section, index) => (
        <SelectSection
          key={index}
          title={section.section}
          showDivider={index < options.length - 1}
        >
          {section.items.map((item) => (
            <SelectItem key={item.value} textValue={item.label}>
              {item.label}
            </SelectItem>
          ))}
        </SelectSection>
      ))}
    </Select>
  );
}
